import { FormRendererV2, type FormRendererV2Props } from '@/Components/form';
import { FormCookieExpirationEnum } from '@/Components/form/utils/form.types';
import { Analytics } from '@/Telemetry';
import { TelemetrySyncSession } from '@/Telemetry/syncSession';
import { getCookie, setCookie } from '@/Utility/cookie';
import { encrypt } from '@/Utility/crypto';
import { useBreakpoints } from '@/Utility/hooks';
import type { Form, RootQueryToHubPageConnectionEdge } from '@/graphql/types';
import { type AppStore, useAppStore } from '@/inertia/AppStore';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { getPlayerRef } from '../MainContainer/Player/Player';

export type Props = {
  location: 'player' | 'sidebar';
};

type GetFormNodeProps = Props & {
  pageType: AppStore['pageType'];
  znglist: AppStore['znglist'] | RootQueryToHubPageConnectionEdge['node'];
  playerNode: AppStore['playerNode'];
};

const getFormNode = ({ location, pageType, znglist, playerNode }: GetFormNodeProps): FormRendererV2Props | null => {
  let form: Form;

  const pageForm =
    (znglist as AppStore['znglist'])?.znglist?.form ??
    (znglist as RootQueryToHubPageConnectionEdge['node'])?.form ??
    null;

  if (['znglist', 'hub', 'front_page'].includes(pageType ?? '') && !!pageForm) {
    form = pageForm.node;

    if (location === 'player' && !!form) {
      return {} as FormRendererV2Props;
    }
  } else {
    form = playerNode?.znglyContent?.form?.node as Form;
    if (location === 'sidebar' && !!form) {
      return {} as FormRendererV2Props;
    }
  }

  if (!form) return null;

  const formState: FormRendererV2Props = {
    formId: form.id,
    schema: form?.schema ?? '{}',
    metadata: {
      ...form?.metadata,
      background: {
        img: form.imageUrl ?? '',
      },
      marketingConsent: form.marketingConsent ?? false,
      password: form.password ?? false,
    },
    uiSchema: form?.uiSchema ?? '{}',
    title: form?.title,
  };

  return formState;
};

const setFormStyle = (location: Props['location'], enable: boolean) => {
  if (location === 'player') {
    const player = getPlayerRef().current?.querySelector?.('.Wrapper') as HTMLDivElement;
    if (!player) return;
    if (enable) {
      player.style.filter = 'blur(2px)';
    } else {
      player.style.filter = '';
    }
    return;
  }
  if (location === 'sidebar') {
    const sidebarContainer = document.querySelectorAll<HTMLDivElement>('.SideBar');
    const list = document.querySelectorAll<HTMLDivElement>('.FormGating_List');
    const grid = document.querySelectorAll<HTMLDivElement>('.FormGating_GridView');

    if (enable) {
      if (!!sidebarContainer?.length) {
        for (const sidebar of sidebarContainer) {
          sidebar.style.height = '600px';
          sidebar.style.overflow = 'hidden';
        }
      }

      if (!!list?.length) {
        for (const l of list) {
          l.style.height = '550px';
          l.style.overflow = 'hidden';
        }
      }
      if (!!grid?.length) {
        for (const g of grid) {
          g.style.height = '550px';
          g.style.overflow = 'hidden';
        }
      }
    } else {
      if (!!sidebarContainer?.length) {
        for (const sidebar of sidebarContainer) {
          sidebar.style.removeProperty('height');
          sidebar.style.removeProperty('overflow');
        }
      }
      if (!!list?.length) {
        for (const l of list) {
          l.style.removeProperty('height');
          l.style.removeProperty('overflow');
        }
      }
      if (!!grid?.length) {
        for (const g of grid) {
          g.style.removeProperty('height');
          g.style.removeProperty('overflow');
        }
      }
    }
  }
};

const FormGating = (props: Props) => {
  const isAppPreview = useAppStore((state) => state.isAppPreview);
  const pageType = useAppStore((state) => state.pageType);
  const znglistNode = useAppStore((state) => state?.znglist);
  const playerNode = useAppStore((state) => state?.playerNode);
  const { isMobile } = useBreakpoints();

  const formNode = useMemo(() => {
    return getFormNode({ location: props.location, pageType, znglist: znglistNode, playerNode });
  }, [props?.location, pageType, znglistNode, playerNode]);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cookieKey = `zngly:form/${formNode?.formId}`;
    if (isAppPreview) {
      cookieKey = `zngly:form/${formNode?.formId}/preview_${Date.now()}`;
    }
    const hasCookie = !!getCookie(cookieKey);

    if (hasCookie === false && formNode?.formId) {
      setOpen(true);
      setFormStyle(props.location, true);
    } else {
      setFormStyle(props.location, false);
    }

    return () => {
      setOpen(false);
      setFormStyle(props.location, false);
    };
  }, [formNode?.formId, isMobile, isAppPreview]); // update on mobile change

  const onCanSubmit = (formData: Record<string, string>, canSubmit: boolean) => {
    if (!canSubmit) {
      return false;
    }

    if (isAppPreview) {
      toast.success('Currently in preview mode. No data will be submitted.');
    }

    return true;
  };

  const onAfterSubmit = async (formData: Record<string, string>) => {
    const value = new URLSearchParams(formData).toString();
    const encrypted = encrypt(value, formNode?.formId ?? ''); // encrypt the value
    const expiryTokens =
      formNode?.metadata?.cookieExpiration?.split(',') ?? FormCookieExpirationEnum.Five_Minutes.split(',');

    // set cookie
    setCookie(`zngly:form/${formNode?.formId}`, encrypted, {
      days: Number.parseInt(expiryTokens?.[0]) ?? 7,
      hours: Number.parseInt(expiryTokens?.[1]) ?? 0,
      domain: 'subDomain',
    });

    // close the form
    setOpen(false);
    setFormStyle(props.location, false);

    // push gtag event
    Analytics.push('zngly_form', {
      eventaction: 'form_submission',
    });

    // sync hubspot
    TelemetrySyncSession.getInstance().syncHubspot(true);
  };

  if (!formNode?.formId || !open) return <></>;
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 100,
      }}
    >
      <div className={'GatedContentContainer'}>
        <FormRendererV2
          {...formNode}
          createFormEntry={isAppPreview === false}
          hooks={{
            afterSubmit: onAfterSubmit,
            canSubmit: onCanSubmit,
          }}
          className={`loc-${props?.location}`}
        />
      </div>
    </div>
  );
};

export default FormGating;
